/**
 * Colours, figures and chrome for the categorical share kinds
 * (`stacked-100-bars`, `grouped-bars`; plots plan L).
 *
 * The plot never assigns a colour by itself: an entity is painted in the slot
 * Python stamped (`colorSlot`), and "Other" (slot null) is neutral. An
 * `ordered` sequence is the one exception the contract names: its categories
 * run light to dark along the sequence, from the deck's first chart colour.
 */

import { getChartInsideLabelColor } from '../../foundations/chart-colors'

import {
  CategoricalBar,
  CategoricalCategory,
  CategoricalRow,
  CategoricalShareData,
} from '@digital-twins/slide-schemas/generated/plot-types'

/** "Other" and any entity without a slot. */
export const CATEGORICAL_NEUTRAL = 'var(--slide-chart-other, #b9c2bf)'

const LIGHT_TEXT = 'var(--slide-cover-text, #ffffff)'
const DARK_TEXT = 'var(--slide-text-primary, #000000)'

/** The lightest and darkest step of the ordered ramp (percent of chart colour 1). */
const RAMP_MIN = 24
const RAMP_MAX = 100
/** At or above this step white text reads on the fill. */
const RAMP_LIGHT_TEXT_FROM = 60

export interface CategoricalFill {
  fill: string
  /** Text colour for a figure drawn inside the fill. */
  text: string
}

export function slotColor(slot: number | null | undefined): string {
  if (typeof slot !== 'number') {
    return CATEGORICAL_NEUTRAL
  }
  return `var(--slide-chart-${slot})`
}

function rampStep(position: number, count: number): number {
  if (count <= 1) {
    return RAMP_MAX
  }
  return Math.round(RAMP_MIN + ((RAMP_MAX - RAMP_MIN) * position) / (count - 1))
}

/** Fill per category id: the ordered ramp by position, else the stamped slot. */
export function categoryFills(data: Pick<CategoricalShareData, 'categories' | 'ordered'>): Map<string, CategoricalFill> {
  const fills = new Map<string, CategoricalFill>()
  const options = data.categories.filter((category) => category.role === 'option')
  data.categories.forEach((category: CategoricalCategory) => {
    if (category.role !== 'option') {
      fills.set(category.id, { fill: CATEGORICAL_NEUTRAL, text: DARK_TEXT })
      return
    }
    if (data.ordered) {
      const step = rampStep(options.indexOf(category), options.length)
      fills.set(category.id, {
        fill: `color-mix(in srgb, var(--slide-chart-1) ${step}%, #ffffff)`,
        text: step >= RAMP_LIGHT_TEXT_FROM ? LIGHT_TEXT : DARK_TEXT,
      })
      return
    }
    const fill = slotColor(category.colorSlot)
    fills.set(category.id, { fill, text: getChartInsideLabelColor(fill) })
  })
  return fills
}

/** A share as printed: Python's decimals, trailing zeros dropped ("13%", "13.4%"), or kept with `fixed` ("13.0%"). Never re-rounded. */
export function formatShare(value: number, decimals: number, locale?: string, fixed = false): string {
  const formatted = new Intl.NumberFormat(locale || undefined, {
    minimumFractionDigits: fixed ? Math.max(0, decimals) : 0,
    maximumFractionDigits: Math.max(0, decimals),
  }).format(value)
  return `${formatted}%`
}

export function fillCategoricalTemplate(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) => (name in values ? String(values[name]) : match))
}

/** The hover text of a name: its full source text when the chart prints a short label. */
export function nameTitle(entity: { label: string; fullLabel?: string }): string {
  return entity.fullLabel || entity.label
}

/**
 * A row carries a picture (Python already applied the Images rule: each row
 * its own), so every row keeps a thumbnail slot and a row without one shows
 * the empty slot.
 */
export function rowsShowImages(rows: Array<{ imageUrl?: string | null }>): boolean {
  return rows.some((row) => typeof row.imageUrl === 'string' && row.imageUrl.length > 0)
}

/** The lightest and darkest neutral tint of an opened Other (percent of the neutral). */
const FOLDED_TINT_MIN = 35
const FOLDED_TINT_MAX = 100
/** The slide surface an opened Other's tints fade into. */
export const FOLDED_SLIDE_SURFACE = 'var(--slide-bg, #ffffff)'

/**
 * Fills of the categories an opened "Other" shows (decision 23): neutral grey
 * tints stepped by folded position, faded into `surface` (the slide's, or a
 * host's own surface token), so Other stays neutral when it opens and the
 * palette's slots stay with the drawn categories. Render only.
 */
export function foldedFills(ids: Array<string>, surface: string = FOLDED_SLIDE_SURFACE): Map<string, CategoricalFill> {
  const fills = new Map<string, CategoricalFill>()
  ids.forEach((id, position) => {
    const step =
      ids.length <= 1
        ? FOLDED_TINT_MAX
        : Math.round(FOLDED_TINT_MAX - ((FOLDED_TINT_MAX - FOLDED_TINT_MIN) * position) / (ids.length - 1))
    fills.set(id, { fill: `color-mix(in srgb, ${CATEGORICAL_NEUTRAL} ${step}%, ${surface})`, text: DARK_TEXT })
  })
  return fills
}

/** What a plot's "Other" folded, when Python stamped it (decision 23); null otherwise. */
export function otherFold(data: Pick<CategoricalShareData, 'categories'>): {
  otherId: string
  folded: Array<{ id: string; label: string; fullLabel?: string }>
  total: number
} | null {
  const other = data.categories.find((category) => category.role === 'other')
  const folded = other?.foldedCategories ?? []
  if (!other || folded.length === 0) {
    return null
  }
  return { otherId: other.id, folded, total: data.categories.length - 1 + folded.length }
}

/**
 * A row's folded bars, one per folded category in its order (decision 23):
 * Python's positional `otherShares` / `otherExactShares` / `otherCounts`, read
 * as stamped (a row stored before `otherExactShares` draws the printed share); null
 * where the row has no figure for that category. Nothing is derived here.
 */
export function foldedRowBars(
  row: Pick<CategoricalRow, 'otherShares' | 'otherExactShares' | 'otherCounts'>,
  folded: Array<{ id: string }>,
): Array<CategoricalBar | null> {
  const shares = row.otherShares ?? []
  const exactShares = row.otherExactShares ?? []
  const counts = row.otherCounts ?? []
  return folded.map((entry, index) => {
    const share = shares[index]
    if (share === null || share === undefined) {
      return null
    }
    return { categoryId: entry.id, share, exactShare: exactShares[index] ?? share, count: counts[index] ?? null }
  })
}

/** Whether a row carries what opens its Other. */
export function rowOpensOther(row: Pick<CategoricalRow, 'otherShares'>): boolean {
  return (row.otherShares ?? []).some((share) => share !== null && share !== undefined)
}
